/**
 * Keysanity Tracker — Small key counters + boss key toggles per dungeon.
 * Ctrl+click toggles MQ mode. Key ring mode fills all keys at once.
 */
import { useTracker } from '@/contexts/tracker-context';
import { DUNGEON_KEYS } from './data/dungeon-keys-data';

export function KeysanityTracker() {
  const { state, dispatch } = useTracker();

  const handleKeyClick = (dungeon: string, maxKeys: number) => {
    const current = state.smallKeys[dungeon] ?? 0;
    const next = state.isKeyring ? (current === 0 ? maxKeys : 0) : Math.min(current + 1, maxKeys);
    dispatch({ type: 'SET_SMALL_KEY', dungeon, count: next });
  };

  const handleKeyRightClick = (e: React.MouseEvent, dungeon: string) => {
    e.preventDefault();
    const current = state.smallKeys[dungeon] ?? 0;
    dispatch({ type: 'SET_SMALL_KEY', dungeon, count: Math.max(0, current - 1) });
  };

  const handleMqToggle = (e: React.MouseEvent, dungeon: string) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      dispatch({ type: 'SET_DUNGEON_MQ', dungeon, isMq: !(state.dungeonMq[dungeon] ?? false) });
    }
  };

  const handleBossKeyClick = (dungeon: string) => {
    dispatch({ type: 'SET_BOSS_KEY', dungeon, obtained: !(state.bossKeys[dungeon] ?? false) });
  };

  return (
    <div className="space-y-1.5">
      {DUNGEON_KEYS.map((dk) => {
        const isMq = state.dungeonMq[dk.dungeon] ?? false;
        const maxKeys = isMq ? dk.nbKeysMq : dk.nbKeysVanilla;
        const currentKeys = state.smallKeys[dk.dungeon] ?? 0;
        const hasBossKey = state.bossKeys[dk.dungeon] ?? false;

        return (
          <div
            key={dk.dungeon}
            className="flex items-center gap-2 py-1 px-2 bg-gray-800/50 rounded-lg"
          >
            {/* Dungeon name + MQ indicator */}
            <span
              className="w-[70px] text-xs font-medium text-gray-300 cursor-pointer select-none truncate"
              onClick={(e) => handleMqToggle(e, dk.dungeon)}
              title="Ctrl+click to toggle MQ"
            >
              {dk.dungeon}
              {isMq && <span className="text-yellow-400 ml-1 text-[10px]">MQ</span>}
            </span>

            {/* Small key image + counter */}
            <div
              className="flex items-center gap-1 cursor-pointer select-none"
              onClick={() => handleKeyClick(dk.dungeon, maxKeys)}
              onContextMenu={(e) => handleKeyRightClick(e, dk.dungeon)}
              title={`${currentKeys}/${maxKeys} keys (right-click to decrease)`}
            >
              <img
                src={dk.smallKeyImg}
                alt={`${dk.dungeon} key`}
                width={20}
                height={20}
                style={{ filter: currentKeys > 0 ? 'none' : 'brightness(0.3)' }}
              />
              <span className={`text-sm font-mono w-[36px] text-center ${currentKeys >= maxKeys ? 'text-green-400' : 'text-gray-300'}`}>
                {currentKeys}/{maxKeys}
              </span>
            </div>

            {/* Boss key */}
            {dk.hasBossKey && (
              <div
                className="cursor-pointer select-none"
                onClick={() => handleBossKeyClick(dk.dungeon)}
                title={hasBossKey ? 'Boss key obtained' : 'No boss key'}
              >
                <img
                  src={dk.bossKeyImg}
                  alt={`${dk.dungeon} boss key`}
                  width={22}
                  height={22}
                  style={{ filter: hasBossKey ? 'none' : 'brightness(0.3)' }}
                />
              </div>
            )}
            {!dk.hasBossKey && <div className="w-[22px]" />}
          </div>
        );
      })}
    </div>
  );
}
